import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    FolderOpen, CheckCircle2, Calendar, Wrench, AlertTriangle,
    Users, FileText, BarChart3, ShieldCheck, ChevronRight, Bell,
    Activity, Clock, PauseCircle, Hourglass, PackageCheck, Send,
    Flag, AlertOctagon, ClipboardList,
} from "lucide-react";
import { HardHat } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import ActivityFeed from "@/components/ActivityFeed";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dashboardDeepLink, stageLabelMap } from "@/lib/stages";
import useSWR from "swr";

/**
 * `/operations` — landing dashboard for the Operations role.
 *
 * OPS-004B · Redesigned around the canonical Operations dashboard
 * aggregate (`GET /api/operations/dashboard/summary`). Every card
 * consumes the payload's exact numbers — no client-side stage/SLA
 * arithmetic — and every card deep-links back to the correctly
 * filtered operational view via `dashboardDeepLink()`.
 */
export default function OperationsDashboard() {
    const { user, hasPermission } = useAuth();
    const [summary, setSummary] = useState(null);
    const [engineers, setEngineers] = useState([]);
    const [engineersErrored, setEngineersErrored] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    // OPS-004B1 · Stage labels come from the canonical backend
    // catalogue — no hardcoded frontend map.
    const { data: stageCatalogue } = useSWR(
        "ops-stages-catalogue",
        () => api.operationsStageCatalogue(),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    );
    const stageLabels = React.useMemo(
        () => stageLabelMap(stageCatalogue),
        [stageCatalogue],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setErrored(false);
        setEngineersErrored(false);
        try {
            const [sum, eng] = await Promise.allSettled([
                api.operationsDashboardSummary(),
                api.engineerWorkloadSummary(),
            ]);
            if (sum.status === "fulfilled") setSummary(sum.value);
            else { setSummary(null); setErrored(true); }
            if (eng.status === "fulfilled") {
                setEngineers(eng.value?.engineers || []);
            } else {
                // MVP-ENG-001 · Surface the failure honestly instead of
                // silently rendering the empty state.
                setEngineers([]);
                setEngineersErrored(true);
            }
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load dashboard");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <OperationsShell>
            <div className="mx-auto max-w-7xl px-4 md:px-8 pt-6 md:pt-10 pb-16" data-testid="ops-dashboard-page">
                {/* DEV-022 · Shared premium PortalHero — same hero
                    pattern as Sales / Field so every role landing
                    reads as one product. */}
                {(() => {
                    const identity = portalIdentity(user?.role);
                    const attentionTotal = summary?.attention?.critical ?? null;
                    const activeProjects = summary?.projects?.active ?? null;
                    const overdue = summary?.stages?.overdue ?? 0;
                    const stalled = summary?.stages?.stalled ?? 0;
                    const exceptions = (attentionTotal ?? 0) + overdue + stalled;
                    return (
                        <div className="mb-6">
                            <PortalHero
                                testid="ops-hero"
                                eyebrow={`${identity.eyebrow} · OVERVIEW`}
                                title={`Welcome, ${(user?.name || "").split(" ")[0] || "Operations"}`}
                                sentence={
                                    loading ? (
                                        <span>Loading your programme…</span>
                                    ) : errored ? (
                                        <span>Dashboard summary is temporarily unavailable — retry below.</span>
                                    ) : (
                                        <span>
                                            You have <strong className="text-white font-semibold">{activeProjects ?? "—"}</strong> active {activeProjects === 1 ? "project" : "projects"}
                                            {" "}and <strong className="text-white font-semibold">{exceptions}</strong> {exceptions === 1 ? "exception" : "exceptions"} needing attention.
                                        </span>
                                    )
                                }
                                cta={{
                                    to: "/operations/attention",
                                    label: "Review Attention Centre",
                                    icon: AlertTriangle,
                                }}
                                ring={{ value: loading ? "—" : exceptions, label: exceptions === 1 ? "exception" : "exceptions" }}
                                tag="Live programme picture"
                            />
                        </div>
                    );
                })()}

                {errored && (
                    <div
                        data-testid="ops-dashboard-error"
                        className="mb-4 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-[12px] text-[#B91C1C]"
                    >
                        Dashboard summary is unavailable right now — cards below fall back to zeros.
                    </div>
                )}

                {/* Top KPI row */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4" data-testid="ops-kpis">
                    <KpiTile
                        label="Active projects"
                        value={summary?.projects?.active}
                        icon={FolderOpen}
                        testid="kpi-active-projects"
                        loading={loading}
                        to={dashboardDeepLink("active_projects")}
                    />
                    <KpiTile
                        label="Overdue stages"
                        value={summary?.stages?.overdue}
                        icon={AlertTriangle}
                        tone={(summary?.stages?.overdue || 0) > 0 ? "amber" : "neutral"}
                        testid="kpi-overdue-stages"
                        loading={loading}
                        to={dashboardDeepLink("overdue_stages")}
                    />
                    <KpiTile
                        label="Stalled stages"
                        value={summary?.stages?.stalled}
                        icon={AlertOctagon}
                        tone={(summary?.stages?.stalled || 0) > 0 ? "red" : "neutral"}
                        testid="kpi-stalled-stages"
                        loading={loading}
                        to={dashboardDeepLink("stalled_stages")}
                    />
                    <KpiTile
                        label="Critical attention"
                        value={summary?.attention?.critical}
                        icon={Bell}
                        tone={(summary?.attention?.critical || 0) > 0 ? "red" : "neutral"}
                        testid="kpi-critical-attention"
                        loading={loading}
                        to={dashboardDeepLink("critical_attention")}
                    />
                    <KpiTile
                        label="Unassigned projects"
                        value={summary?.projects?.unassigned_active}
                        icon={Users}
                        tone={(summary?.projects?.unassigned_active || 0) > 0 ? "amber" : "neutral"}
                        testid="kpi-unassigned-projects"
                        loading={loading}
                        to={dashboardDeepLink("unassigned_projects")}
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-6">
                    <div className="lg:col-span-2 space-y-4">
                        {/* Projects by lifecycle stage */}
                        <ByStageCard
                            loading={loading}
                            rows={summary?.projects?.by_stage || []}
                            stageLabels={stageLabels}
                        />

                        {/* Surveys + upcoming visits + logistics + notifications */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ListCard
                                icon={FileText}
                                title="Surveys awaiting Operations"
                                testid="ops-dash-surveys-card"
                                items={[
                                    {
                                        label: "Ready for review",
                                        value: summary?.surveys?.requiring_review,
                                        to: dashboardDeepLink("surveys_review"),
                                        testid: "ops-dash-surveys-review",
                                    },
                                    {
                                        label: "Clarification requested",
                                        value: summary?.surveys?.requiring_clarification,
                                        to: dashboardDeepLink("surveys_clarification"),
                                        testid: "ops-dash-surveys-clarification",
                                    },
                                    {
                                        label: "Awaiting scheduling",
                                        value: null,
                                        to: dashboardDeepLink("surveys_scheduling"),
                                        testid: "ops-dash-surveys-scheduling",
                                        hint: "Open the Site Survey queue",
                                    },
                                ]}
                                loading={loading}
                            />
                            <ListCard
                                icon={Calendar}
                                title="Upcoming visits"
                                testid="ops-dash-upcoming-card"
                                items={[
                                    {
                                        label: "Upcoming surveys",
                                        value: summary?.upcoming?.surveys,
                                        to: dashboardDeepLink("upcoming_surveys"),
                                        testid: "ops-dash-upcoming-surveys",
                                    },
                                    {
                                        label: "Upcoming installations",
                                        value: summary?.upcoming?.installations,
                                        to: dashboardDeepLink("upcoming_installations"),
                                        testid: "ops-dash-upcoming-installations",
                                    },
                                ]}
                                loading={loading}
                            />
                            <ListCard
                                icon={PackageCheck}
                                title="Logistics"
                                testid="ops-dash-logistics-card"
                                items={[
                                    {
                                        label: "Outstanding deliveries",
                                        value: summary?.logistics?.outstanding,
                                        to: dashboardDeepLink("logistics_outstanding"),
                                        testid: "ops-dash-logistics-outstanding",
                                    },
                                ]}
                                loading={loading}
                            />
                            <ListCard
                                icon={Send}
                                title="Notification health"
                                testid="ops-dash-notifications-card"
                                items={[
                                    {
                                        label: "Failed (7d)",
                                        value: summary?.notifications?.failed_7d,
                                        to: dashboardDeepLink("notifications_failing"),
                                        testid: "ops-dash-notifications-failed",
                                        tone: (summary?.notifications?.failed_7d || 0) > 0 ? "amber" : "neutral",
                                    },
                                    {
                                        label: "Retryable (7d)",
                                        value: summary?.notifications?.retryable_7d,
                                        to: dashboardDeepLink("notifications_retryable"),
                                        testid: "ops-dash-notifications-retryable",
                                    },
                                ]}
                                loading={loading}
                            />
                        </div>

                        {/* Recently completed */}
                        <ListCard
                            icon={CheckCircle2}
                            title="Recently completed"
                            testid="ops-dash-recent-card"
                            items={[
                                {
                                    label: "Completed in last 30 days",
                                    value: summary?.projects?.recently_completed_30d,
                                    to: dashboardDeepLink("recently_completed"),
                                    testid: "ops-dash-recent-completed",
                                },
                            ]}
                            loading={loading}
                        />

                        {/* Activity feed */}
                        <div data-testid="ops-activity">
                            <ActivityFeed
                                variant="operations"
                                testidPrefix="ops-activity-feed"
                                initialLimit={5}
                                collapsible
                            />
                        </div>
                    </div>

                    {/* Right rail — engineer workload + primary nav */}
                    <div className="space-y-3" data-testid="ops-quick-nav">
                        <div
                            className="rounded-xl border border-[#E5E7EB] bg-white p-4"
                            data-testid="ops-dash-workload-card"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <HardHat size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                                <div className="text-sm font-semibold text-[#131B2F]">Resource workload</div>
                            </div>
                            {loading ? (
                                <div className="text-[12px] text-[#4B5565]">Loading…</div>
                            ) : engineersErrored ? (
                                <div
                                    data-testid="ops-dash-workload-error"
                                    className="text-[12px] text-[#B45309]"
                                >
                                    Couldn't load resource workload. Retry from Resource Management.
                                </div>
                            ) : engineers.length === 0 ? (
                                <div
                                    data-testid="ops-dash-workload-empty"
                                    className="text-[12px] text-[#4B5565]"
                                >
                                    No resources configured yet.
                                </div>
                            ) : (
                                <ul className="space-y-1.5" data-testid="ops-dash-workload-list">
                                    {engineers.slice(0, 6).map((e) => (
                                        <li
                                            key={e.id}
                                            className="flex items-center justify-between text-[12px]"
                                            data-testid={`ops-dash-workload-row-${e.id}`}
                                        >
                                            <Link
                                                to={`/operations/resources?tab=workload&highlight=${encodeURIComponent(e.id)}`}
                                                className="text-[#131B2F] truncate mr-2 hover:underline"
                                                data-testid={`ops-dash-workload-row-link-${e.id}`}
                                            >
                                                {e.displayName || e.name || "Resource"}
                                            </Link>
                                            <span className="text-[#4B5565] tabular-nums">
                                                {(e.openTaskCount ?? e.openTasks ?? e.open_tasks ?? 0)} open
                                                {(e.overdueTaskCount ?? 0) > 0 && (
                                                    <span className="text-[#B45309]"> · {e.overdueTaskCount} overdue</span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <Link
                                to="/operations/resources?tab=workload"
                                data-testid="ops-dash-workload-link"
                                className="mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-[#131B2F] hover:underline"
                            >
                                Open Resource Management <ChevronRight size={11} strokeWidth={2} />
                            </Link>
                        </div>

                        <NavCard to="/operations/attention"     icon={Bell}        label="Attention Centre"  hint="Rule-generated exceptions needing action" testid="ops-nav-card-attention" tone="attention" />
                        <NavCard to="/operations/notifications?tab=deliveries&page=1" icon={Bell} label="Notification Centre" hint="Delivery health, failures and retries" testid="ops-nav-card-notifications" />
                        <NavCard to="/operations/customers"  icon={Users}       label="Customers"  hint="Homeowners and their accounts" testid="ops-nav-card-customers" />
                        <NavCard to="/operations/projects"   icon={FolderOpen}  label="Projects"   hint="Live installations across the fleet" testid="ops-nav-card-projects" />
                        <NavCard to="/operations/calendar"   icon={Calendar}    label="Calendar"   hint="Visits, availability and clashes" testid="ops-nav-card-calendar" />
                        <NavCard to="/operations/tasks"      icon={ClipboardList} label="Tasks"    hint="Cross-project task workspace" testid="ops-nav-card-tasks" />
                        <NavCard to="/operations/resources"  icon={HardHat} label="Resource Management"  hint="Team, roles and workload" testid="ops-nav-card-engineers" />
                        <NavCard to="/operations/documents"  icon={FileText}    label="Documents"  hint="Site surveys, contracts, sign-offs" testid="ops-nav-card-documents" />
                        <NavCard to="/operations/reports"    icon={BarChart3}   label="Reports"    hint="Fleet-wide KPIs, workload and exceptions" testid="ops-nav-card-reports" />
                        {(hasPermission("manage_users") || hasPermission("manage_templates") || hasPermission("manage_system_settings")) && (
                            <NavCard
                                to="/operations/administration"
                                icon={ShieldCheck}
                                label="Administration"
                                hint="Users, templates, system settings"
                                testid="ops-nav-card-administration"
                                tone="admin"
                            />
                        )}
                    </div>
                </div>
            </div>
        </OperationsShell>
    );
}

function ByStageCard({ loading, rows, stageLabels }) {
    const total = (rows || []).reduce((n, r) => n + (r.count || 0), 0);
    return (
        <section
            className="rounded-xl border border-[#E5E7EB] bg-white p-5"
            data-testid="ops-dash-by-stage-card"
        >
            <div className="flex items-center gap-2 mb-3">
                <Flag size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                <div className="text-sm font-semibold text-[#131B2F]">Projects by lifecycle stage</div>
                <span className="text-[11px] text-[#4B5565] ml-auto">{total} active</span>
            </div>
            {loading ? (
                <div className="text-[12px] text-[#4B5565]">Loading…</div>
            ) : total === 0 ? (
                <div
                    data-testid="ops-dash-by-stage-empty"
                    className="text-[12px] text-[#4B5565]"
                >
                    No active projects — start one from the Projects page.
                </div>
            ) : (
                <ul className="space-y-1.5" data-testid="ops-dash-by-stage-list">
                    {(rows || []).map((r) => {
                        const pct = total === 0 ? 0 : Math.round((r.count / total) * 100);
                        return (
                            <li
                                key={r.stage}
                                data-testid={`ops-dash-by-stage-row-${r.stage}`}
                                className="flex items-center gap-3"
                            >
                                <Link
                                    to={dashboardDeepLink("projects_by_stage", { stage: r.stage })}
                                    data-testid={`ops-dash-by-stage-link-${r.stage}`}
                                    className="flex-1 min-w-0 text-[12px] text-[#131B2F] hover:underline truncate"
                                >
                                    {/* OPS-004B2 · Prefer the server-hydrated
                                        `stage_label`; only fall back to the
                                        SWR-loaded catalogue map or the raw
                                        canonical key if the summary itself
                                        somehow returned neither (never
                                        expected under the current contract). */}
                                    {r.stage_label
                                        || (stageLabels && stageLabels[r.stage])
                                        || r.stage}
                                </Link>
                                <div className="w-40 h-1.5 rounded-full bg-[#F0F1F5] overflow-hidden">
                                    <div className="h-full bg-[#131B2F]" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-8 text-right text-[11px] text-[#4B5565] tabular-nums">
                                    {r.count}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function ListCard({ icon: Icon, title, items, loading, testid }) {
    const anyValue = (items || []).some(
        (i) => i.value != null || i.hint,
    );
    return (
        <section
            className="rounded-xl border border-[#E5E7EB] bg-white p-4"
            data-testid={testid}
        >
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                <div className="text-sm font-semibold text-[#131B2F]">{title}</div>
            </div>
            {loading ? (
                <div className="text-[12px] text-[#4B5565]">Loading…</div>
            ) : !anyValue ? (
                <div
                    data-testid={`${testid}-empty`}
                    className="text-[12px] text-[#4B5565]"
                >
                    Nothing outstanding.
                </div>
            ) : (
                <ul className="space-y-1.5">
                    {(items || []).map((it, idx) => (
                        <li
                            key={it.testid || it.label || idx}
                            className="flex items-center justify-between"
                            data-testid={`${it.testid}-row`}
                        >
                            <Link
                                to={it.to}
                                data-testid={it.testid}
                                className={`text-[12px] hover:underline truncate flex-1 min-w-0 ${
                                    it.tone === "amber" ? "text-[#B45309]"
                                        : it.tone === "red" ? "text-[#B91C1C]"
                                        : "text-[#131B2F]"
                                }`}
                            >
                                {it.label}
                                {it.hint && (
                                    <span className="ml-1 text-[11px] text-[#4B5565]">
                                        · {it.hint}
                                    </span>
                                )}
                            </Link>
                            {it.value != null && (
                                <span
                                    className="ml-2 tabular-nums text-[12px] text-[#131B2F] font-medium"
                                    data-testid={`${it.testid}-value`}
                                >
                                    {it.value ?? 0}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function KpiTile({ label, value, icon: Icon, tone = "neutral", testid, loading, to }) {
    const cls = tone === "amber"
        ? "border-[#F59E0B]/40 bg-[#FEF3C7]/40 hover:bg-[#FEF3C7]/60"
        : tone === "red"
        ? "border-[#FCA5A5] bg-[#FEE2E2]/40 hover:bg-[#FEE2E2]/60"
        : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]";
    const textCls = tone === "amber" ? "text-[#B45309]"
                  : tone === "red"   ? "text-[#B91C1C]"
                  : "text-[#131B2F]";
    const body = (
        <>
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">{label}</div>
                <Icon size={14} strokeWidth={1.75} className={textCls} />
            </div>
            <div className={`mt-2 text-3xl font-semibold ${textCls}`}>
                {loading ? "—" : (value ?? 0)}
            </div>
        </>
    );
    if (!to) return <div className={`rounded-xl border ${cls} p-4`} data-testid={testid}>{body}</div>;
    return (
        <Link to={to} data-testid={testid} className={`rounded-xl border ${cls} p-4 block transition-colors focus:outline-none focus:ring-2 focus:ring-[#131B2F]`}>
            {body}
        </Link>
    );
}

function NavCard({ to, icon: Icon, label, hint, testid, tone }) {
    // OPS-UX-001 · Every shortcut card shares the same light surface,
    // border, radius, shadow, padding, hover state, typography and
    // chevron alignment. Attention and Administration only differ in
    // their icon tile accents:
    //   • attention  → subtle amber tile keeps the warning semantic;
    //   • admin      → neutral slate/blue tile with zero urgency;
    //   • default    → the existing Pearl-blue tile.
    const iconClass =
        tone === "attention"
            ? "bg-[#FEF3C7] text-[#B45309] ring-1 ring-[#F59E0B]/40"
            : tone === "admin"
                ? "bg-[#E2E8F0] text-[#334155] ring-1 ring-[#94A3B8]/40"
                : "bg-[#E1E6F0] text-[#131B2F]";
    return (
        <Link
            to={to}
            data-testid={testid}
            data-tone={tone || "default"}
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-[#131B2F] shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-colors hover:border-[#131B2F] hover:bg-[#F9FAFB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#131B2F]"
        >
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${iconClass}`}>
                <Icon size={16} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-[11px] text-[#4B5565] truncate">{hint}</div>
            </div>
            <ChevronRight size={14} strokeWidth={1.75} className="text-[#4B5565]" />
        </Link>
    );
}
